import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Import components
import FilterBar from '../components/cat/FilterBar';
import NPSScore from '../components/cat/NPSScore';
import SentimentOverview from '../components/cat/SentimentOverview';
import CategoryBreakdown from '../components/cat/CategoryBreakdown';
import SubcategoryAnalysis from '../components/cat/SubcategoryAnalysis';
import EntityAnalysis from '../components/cat/EntityAnalysis';
import AIInsights from '../components/cat/AIInsights';
import TopPainPoints from '../components/cat/TopPainPoints';
import CategoryInsightsAnalysis from '../components/cat/CategoryInsightsAnalysis';
import CategoryHeatmap from '../components/cat/CategoryHeatmap';
// import FeedbackTable from '../components/cat/FeedbackTable';
import SentimentByCategory from '../components/cat/SentimentByCategory';
import CategoryInsights from '../components/cat/CategoryInsights';
import GlobalAnalytics from '../components/cat/GlobalAnalytics';
import { useData } from '../contexts/DataContext';

const Dashboard = () => {
  const { data: bankData } = useData(); // Get data from context
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [activeTab, setActiveTab] = useState('category'); // New state for tab management

  // Process data when bankData is available
  useEffect(() => {
    const processData = async () => {
      if (!bankData) return;
      
      try {
        setLoading(true);
        
        // Process data for dashboard
        const processedData = {
          metadata: bankData.metadata,
          
          // Sentiment distribution
          sentimentDistribution: {},
          
          // Category counts
          categoryCounts: {},
          
          // Top subcategories
          topSubcategories: [],
          
          // Sentiment by category
          sentimentByCategory: {},
          
          // Named entities
          namedEntities: {},
          
          // NPS Score
          npsScore: 0,
          
          // Recent feedback samples
          recentFeedback: []
        };

        // Calculate sentiment distribution
        const sentimentCounts = {};
        bankData.data.forEach(item => {
          const sentiment = item.sentiment;
          sentimentCounts[sentiment] = (sentimentCounts[sentiment] || 0) + 1;
        });
        processedData.sentimentDistribution = sentimentCounts;

        // Calculate NPS Score
        const promoters = sentimentCounts.Promoter || 0;
        const detractors = sentimentCounts.Detractor || 0;
        const total = bankData.data.length;
        processedData.npsScore = Math.round(((promoters / total) - (detractors / total)) * 100);

        // Calculate category counts (count unique feedback items per category, not occurrences)
        const categoryCounts = {};
        bankData.data.forEach(item => {
          if (item.processed_data && item.processed_data.categories) {
            // Get unique categories mentioned in this feedback item
            const uniqueCategories = [...new Set(item.processed_data.categories.map(cat => cat.category))];
            uniqueCategories.forEach(cat => {
              categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
            });
          }
        });
        processedData.categoryCounts = categoryCounts;

        // Calculate top subcategories with sentiment breakdown
        const subcategoryCounts = {};
        const subcategorySentiments = {};
        
        bankData.data.forEach(item => {
          if (item.processed_data && item.processed_data.subcategories) {
            item.processed_data.subcategories.forEach(subcategory => {
              const parentCat = subcategory.category;
              const subCat = subcategory.subcategory;
              const sentiment = subcategory.sentiment;
              const key = `${parentCat}:${subCat}`;
              
              // Count total mentions
              subcategoryCounts[key] = (subcategoryCounts[key] || 0) + 1;
              
              // Track sentiment counts
              if (!subcategorySentiments[key]) {
                subcategorySentiments[key] = { positive: 0, negative: 0, neutral: 0 };
              }
              
              subcategorySentiments[key][sentiment]++;
            });
          }
        });

        // Prepare subcategory data with sentiment breakdowns
        processedData.topSubcategories = Object.entries(subcategoryCounts)
          .map(([name, value]) => {
            const sentiments = subcategorySentiments[name] || { positive: 0, negative: 0, neutral: 0 };
            return {
              name,
              value,
              positive: sentiments.positive || 0,
              negative: sentiments.negative || 0,
              neutral: sentiments.neutral || 0
            };
          })
          .sort((a, b) => b.value - a.value);

        // Calculate sentiment by category (count unique feedback items per category-sentiment)
        const sentimentByCategory = {};
        bankData.data.forEach(item => {
          if (item.processed_data && item.processed_data.categories) {
            // Get unique categories mentioned in this feedback item with their sentiments
            const categoryMap = new Map();
            item.processed_data.categories.forEach(category => {
              categoryMap.set(category.category, category.sentiment);
            });
            
            // Count each feedback item once per unique category
            categoryMap.forEach((sentiment, cat) => {
              if (!sentimentByCategory[cat]) {
                sentimentByCategory[cat] = { positive: 0, negative: 0, neutral: 0 };
              }
              
              sentimentByCategory[cat][sentiment]++;
            });
          }
        });
        processedData.sentimentByCategory = sentimentByCategory;

        // Process named entities
        const namedEntities = {};
        bankData.data.forEach(item => {
          if (item.processed_data && item.processed_data.named_entities) {
            item.processed_data.named_entities.forEach(entity => {
              const name = entity.entity;
              const type = entity.type;
              const sentiment = entity.sentiment;
              
              if (!namedEntities[type]) {
                namedEntities[type] = {};
              }
              
              if (!namedEntities[type][name]) {
                namedEntities[type][name] = { count: 0, positive: 0, negative: 0, neutral: 0 };
              }
              
              namedEntities[type][name].count++;
              
              if (sentiment === 'positive') namedEntities[type][name].positive++;
              else if (sentiment === 'negative') namedEntities[type][name].negative++;
              else namedEntities[type][name].neutral++;
            });
          }
        });

        // Convert to array format
        const topEntitiesByType = {};
        Object.keys(namedEntities).forEach(type => {
          topEntitiesByType[type] = Object.entries(namedEntities[type])
            .map(([name, stats]) => ({ 
              name, 
              count: stats.count,
              positive: stats.positive,
              negative: stats.negative,
              neutral: stats.neutral
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        });

        processedData.namedEntities = topEntitiesByType;

        // Get sample recent feedback
        processedData.recentFeedback = bankData.data
          .map(item => {
            const categories = item.processed_data && item.processed_data.categories 
              ? item.processed_data.categories.map(c => c.category) 
              : [];
            
            return {
              id: Math.random().toString(36).substr(2, 9),
              sentiment: item.sentiment,
              text: item.text,
              categories: categories
            };
          })
          .slice(0, 15);

        setData(processedData);
        setLoading(false);
      } catch (error) {
        console.error('Error processing data:', error);
        setLoading(false);
      }
    };

    processData();
  }, [bankData]);

  const formatCategoryData = () => {
    if (!data || !bankData) return [];
    
    // Calculate sentiment breakdown the same way as CategoryInsightsAnalysis
    return Object.entries(data.categoryCounts)
      .filter(([category]) => category !== 'Others') // Filter out "Others" category
      .map(([category, count]) => {
      // Filter feedback items that mention this category (same as CategoryInsightsAnalysis)
      const categoryFeedback = bankData.data.filter(item => 
        item.processed_data?.categories?.some(cat => cat.category === category)
      );
      
      // Calculate sentiment breakdown (same logic as CategoryInsightsAnalysis)
      const positiveFeedback = categoryFeedback.filter(item => {
        const categoryData = item.processed_data.categories.find(cat => cat.category === category);
        return categoryData?.sentiment === 'positive';
      });
      
      const negativeFeedback = categoryFeedback.filter(item => {
        const categoryData = item.processed_data.categories.find(cat => cat.category === category);
        return categoryData?.sentiment === 'negative';
      });
      
      const neutralFeedback = categoryFeedback.filter(item => {
        const categoryData = item.processed_data.categories.find(cat => cat.category === category);
        return categoryData?.sentiment === 'neutral';
      });
      
      return {
        name: category.replace('_', ' '),
        value: count,
        positive: positiveFeedback.length,
        negative: negativeFeedback.length,
        neutral: neutralFeedback.length
      };
    }).sort((a, b) => b.value - a.value);
  };

  const formatSentimentByCategoryData = () => {
    if (!data) return [];
    
    return Object.entries(data.sentimentByCategory).map(([category, sentiments]) => ({
      name: category.replace('_', ' '),
      positive: sentiments.positive,
      negative: sentiments.negative,
      neutral: sentiments.neutral
    })).sort((a, b) => (b.positive + b.negative + b.neutral) - (a.positive + a.negative + a.neutral));
  };

  // Show loading if data is not ready
  if (!bankData || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <main className="">
        <div className="mb-8">
          <FilterBar 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            categories={data.metadata.categories}
          />
        </div>
        
        {/* NPS Score and Sentiment Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          {/* <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-1"
          >
            <NPSScore data={data} />
          </motion.div>
           */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-2"
          >
            <SentimentOverview data={data.sentimentDistribution} />
          </motion.div>
          
          {/* Tabbed Category/Subcategory Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-3"
          >
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Tab Headers */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('category')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'category'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Category Breakdown
                  </button>
                  <button
                    onClick={() => setActiveTab('subcategory')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'subcategory'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Subcategory Analysis
                  </button>
                </nav>
              </div>
              
              {/* Tab Content */}
              <div className="p-0">
                {activeTab === 'category' && (
                  <CategoryBreakdown data={formatCategoryData()} />
                )}
                {activeTab === 'subcategory' && (
                  <SubcategoryAnalysis data={data.topSubcategories} fullData={bankData} />
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* AI Insights */}
        {/* <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
          >
            <AIInsights data={DATA} />
          </motion.div>
        </div> */}
        
        {/* Category Insights Analysis */}
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
          >
            <CategoryInsightsAnalysis data={bankData} />
          </motion.div>
        </div>
        
        {/* Category Heatmap */}
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <CategoryHeatmap data={bankData} />
          </motion.div>
        </div>

        {/* Global Analytics - Demographics and Channel Performance */}
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <GlobalAnalytics data={bankData} />
          </motion.div>
        </div>

         {/* Top Pain Points Analysis */}
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <TopPainPoints data={bankData} />
          </motion.div>
        </div>
        
        
        {/* Category Breakdown and Subcategory Analysis - MOVED TO TOP */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <CategoryBreakdown data={formatCategoryData()} />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.75 }}
          >
            <SubcategoryAnalysis data={data.topSubcategories} fullData={bankData} />
          </motion.div>
        </div> */}
        
        {/* Category Insights */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <CategoryInsights data={bankData} />
          </motion.div>
        </div>
        
        {/* Sentiment by Category */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.85 }}
          >
            <SentimentByCategory data={formatSentimentByCategoryData()} />
          </motion.div>
        </div>
        
        {/* Entity Analysis */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.85 }}
          >
            <EntityAnalysis data={data.namedEntities} />
          </motion.div>
        </div>
        
        
        {/* Recent Feedback Table */}
        {/* <div className="grid grid-cols-1 gap-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <FeedbackTable data={data.recentFeedback} />
          </motion.div>
        </div> */}
      </main>
    </div>
  );
};

export default Dashboard;